import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { subscribedToAddOnFeature } from "reduxSlices/addOnFeatureSubscriptionsSlice"
import {
  getFormRequiresAuthentication,
  getFormSlug,
  getFormTracksSubmissions,
  getFormUsage,
  setFormRequiresAuthWithDefaultTrackingSetting,
  setFormTracksSubmissions,
} from "reduxSlices/formBuilderSlice"
import * as API from "services/api"
import { AUTHENTICATED_FORMS_FEATURE_NAME } from "utils/addOnFeatureHelpers"
import { USAGES_TO_EXCLUDE_FORM_REQUIRES_AUTHENTICATION } from "utils/formHelpers"
import Switch from "shared/Switch"
import { errorToast } from "components/shared/toast"
import ContentBar from "shared/lists/ContentBar"
import FormTracksSubmissionsSetting from "./FormTracksSubmissionsSetting"

const TITLE = "Require Authentication"
const DESCRIPTION = "When required, a user must log in to view and submit form."

const FormRequiresAuthenticationSetting = () => {
  const dispatch = useDispatch()

  const formRequiresAuthentication = useSelector(getFormRequiresAuthentication)
  const formTracksSubmissions = useSelector(getFormTracksSubmissions)
  const formSlug = useSelector(getFormSlug)
  const formUsage = useSelector(getFormUsage)

  const isSubscribedToAuthenticatedForms = useSelector(subscribedToAddOnFeature(AUTHENTICATED_FORMS_FEATURE_NAME))

  if (!isSubscribedToAuthenticatedForms) return null
  if (USAGES_TO_EXCLUDE_FORM_REQUIRES_AUTHENTICATION.includes(formUsage)) return null

  const updateFormTracksSubmissions = async (tracksSubmissions) => {
    const response = await API.updateFormTracksSubmissions({ formSlug, tracksSubmissions })

    if (response.ok) {
      dispatch(setFormTracksSubmissions(tracksSubmissions))
    } else {
      errorToast(`Unable to update the submission tracking setting. ${response.data.errors.join(", ")}`)
    }
  }

  const updateFormRequiresAuthentication = async (requiresAuthentication) => {
    const response = await API.updateFormRequiresAuthentication({ formSlug, requiresAuthentication })

    if (response.ok) {
      dispatch(setFormRequiresAuthWithDefaultTrackingSetting(requiresAuthentication))
    } else {
      errorToast(`Unable to update authentication setting. ${response.data.errors.join(", ")}`)
    }
  }

  return (
    <ContentBar
      title={TITLE}
      description={DESCRIPTION}
      footer={(
        <FormTracksSubmissionsSetting
          formRequiresAuthentication={formRequiresAuthentication}
          formTracksSubmissions={formTracksSubmissions}
          updateFormTracksSubmissions={updateFormTracksSubmissions}
        />
      )}
    >
      <Switch
        aria-label={TITLE}
        checked={formRequiresAuthentication}
        onChange={updateFormRequiresAuthentication}
      />
    </ContentBar>
  )
}

export default FormRequiresAuthenticationSetting
